import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Smartphone, QrCode, Send, Pause, Play, Trash2, 
  Plus, Edit2, X, Loader2, Check, AlertTriangle, Settings, 
  Users, Clock, RefreshCw, CheckCircle, XCircle, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppAdmin({ credentials, leads }) {
  // Connection state
  const [waStatus, setWaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', content: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Queue
  const [queue, setQueue] = useState({ queue: [], pending: 0, sent: 0, failed: 0 });
  
  // Broadcast
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);
  const [filterTag, setFilterTag] = useState('all'); // all, pine, tester
  const [filterDate, setFilterDate] = useState('all'); // all, today, week
  
  // Settings
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    dailyLimit: 200,
    minDelay: 45,
    maxDelay: 90,
    businessHoursStart: 9,
    businessHoursEnd: 21,
    enabled: true
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Test message
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const getAuthHeaders = () => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return {
      'Authorization': `Basic ${auth}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch status
  const fetchStatus = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/status`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setWaStatus(data);
        if (data.settings) setSettings(data.settings);
      }
    } catch (err) {
      console.error('Failed to fetch WA status:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/templates`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  // Fetch queue
  const fetchQueue = async () => {
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/queue`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setQueue(data);
      }
    } catch (err) {
      console.error('Failed to fetch queue:', err);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchTemplates();
    fetchQueue();
  }, []);

  // Only poll when connecting (waiting for QR scan), stop when connected
  useEffect(() => {
    // Don't poll if connected or offline
    if (waStatus?.status === 'connected' || waStatus?.status === 'service_offline') {
      return;
    }
    
    // Poll every 5 seconds only when connecting
    const interval = setInterval(() => {
      fetchStatus(true);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [waStatus?.status]);

  // Manual refresh handler
  const handleManualRefresh = () => {
    fetchStatus();
    fetchQueue();
  };

  // Logout
  const handleLogout = async () => {
    if (!window.confirm('This will disconnect your WhatsApp. Continue?')) return;
    try {
      await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      fetchStatus();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // Save template
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name || !templateForm.content) return;
    
    setSavingTemplate(true);
    try {
      const url = editingTemplate 
        ? `${API_URL}/api/strategy-lab/admin/whatsapp/templates/${editingTemplate.id}`
        : `${API_URL}/api/strategy-lab/admin/whatsapp/templates`;
      
      const res = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(templateForm)
      });
      
      if (res.ok) {
        setShowTemplateForm(false);
        setEditingTemplate(null);
        setTemplateForm({ name: '', content: '' });
        fetchTemplates();
      }
    } catch (err) {
      console.error('Failed to save template:', err);
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/templates/${templateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/settings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings)
      });
      setShowSettings(false);
      fetchStatus();
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  // Pause/Resume queue
  const toggleQueue = async (pause) => {
    try {
      await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/queue/${pause ? 'pause' : 'resume'}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      fetchStatus();
    } catch (err) {
      console.error('Failed to toggle queue:', err);
    }
  };

  // Force process queue
  const forceProcessQueue = async () => {
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/queue/force`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      alert(data.message || 'Queue processing triggered');
      fetchStatus();
      fetchQueue();
    } catch (err) {
      console.error('Failed to force queue:', err);
      alert('Failed to trigger queue');
    }
  };

  // Clear queue
  const clearQueue = async () => {
    if (!window.confirm('Clear all pending messages?')) return;
    try {
      await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/queue`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchQueue();
    } catch (err) {
      console.error('Failed to clear queue:', err);
    }
  };

  // Send broadcast
  const handleBroadcast = async () => {
    if (!selectedTemplateId || selectedLeadIds.length === 0) return;
    
    setBroadcasting(true);
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/broadcast`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          lead_ids: selectedLeadIds,
          template_id: selectedTemplateId,
          scheduled_at: scheduledAt || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(`Success! ${data.queued} messages queued for broadcast.`);
        setShowBroadcastModal(false);
        setSelectedLeadIds([]);
        setSelectedTemplateId('');
        setScheduledAt('');
        fetchQueue();
      } else {
        const err = await res.json();
        alert(`Failed: ${err.detail}`);
      }
    } catch (err) {
      console.error('Broadcast failed:', err);
      alert('Broadcast failed. Please try again.');
    } finally {
      setBroadcasting(false);
    }
  };

  // Send test message
  const handleSendTest = async () => {
    if (!testPhone || !testMessage) return;
    
    setSendingTest(true);
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/admin/whatsapp/test`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ phone: testPhone, message: testMessage })
      });
      
      if (res.ok) {
        alert('Test message sent successfully!');
        setShowTestModal(false);
        setTestPhone('');
        setTestMessage('');
      } else {
        const err = await res.json();
        alert(`Failed: ${err.detail || err.error}`);
      }
    } catch (err) {
      alert('Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  // Toggle lead selection
  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Helper to get phone from lead (handles multiple field names)
  const getLeadPhone = (lead) => {
    if (lead.phones && lead.phones.length > 0) return lead.phones[0];
    if (lead.phone) return lead.phone;
    if (lead.phone_raw) return lead.phone_raw;
    return null;
  };

  // Check if lead has phone
  const leadHasPhone = (lead) => {
    return !!(getLeadPhone(lead));
  };

  // Filter leads by tag and date
  const getFilteredLeads = () => {
    let filtered = leads;
    
    // Filter by tag
    if (filterTag === 'pine') {
      filtered = filtered.filter(l => l.tags?.includes('pine_script') || l.tag === 'pine_script');
    } else if (filterTag === 'tester') {
      filtered = filtered.filter(l => l.tags?.includes('tester') || l.tag === 'tester');
    }
    
    // Filter by date
    if (filterDate === 'today') {
      const today = new Date().toDateString();
      filtered = filtered.filter(l => {
        const leadDate = new Date(l.created_at || l.createdAt).toDateString();
        return leadDate === today;
      });
    } else if (filterDate === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(l => {
        const leadDate = new Date(l.created_at || l.createdAt);
        return leadDate >= weekAgo;
      });
    }
    
    return filtered;
  };

  // Select all filtered leads with phones
  const selectAllLeads = () => {
    const filtered = getFilteredLeads();
    const leadsWithPhones = filtered.filter(leadHasPhone);
    setSelectedLeadIds(leadsWithPhones.map(l => l.id));
  };

  // Select none
  const selectNone = () => {
    setSelectedLeadIds([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#25D366] animate-spin" />
      </div>
    );
  }

  const isConnected = waStatus?.status === 'connected';
  const isConnecting = waStatus?.status === 'connecting';
  const isOffline = waStatus?.status === 'service_offline';
  const hasError = waStatus?.lastError;
  const diagnostics = waStatus?.diagnostics || {};
  const conflictActive = diagnostics.conflictActive;

  return (
    <div className="space-y-6">
      {/* Stream Conflict Warning */}
      {conflictActive && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-orange-400 font-semibold text-sm">Session Conflict Detected</p>
              <p className="text-orange-300/80 text-xs mt-1">
                Another WhatsApp Web session is fighting for this account. The connection will keep dropping until you remove the other session.
              </p>
              <p className="text-orange-200/60 text-xs mt-2 font-medium">
                Fix: Open WhatsApp on your phone → Settings → Linked Devices → Remove all other devices except this one
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Connection Error Alert */}
      {hasError && !isConnected && !conflictActive && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium text-sm">Connection Issue</p>
            <p className="text-red-300/70 text-xs mt-1">{waStatus.lastError}</p>
            {diagnostics.reconnectAttempts > 0 && (
              <p className="text-red-300/50 text-xs mt-1">
                Reconnect attempts: {diagnostics.reconnectAttempts}/{diagnostics.maxReconnectAttempts}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Connection Status Card */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isConnected ? 'bg-[#25D366]/20' : 
              conflictActive ? 'bg-orange-500/20' :
              isOffline ? 'bg-red-500/20' : 'bg-amber-500/20'
            }`}>
              <MessageSquare className={`w-5 h-5 ${
                isConnected ? 'text-[#25D366]' : 
                conflictActive ? 'text-orange-400' :
                isOffline ? 'text-red-400' : 'text-amber-400'
              }`} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-white">WhatsApp Broadcast</h3>
              <p className="text-sm text-white/50">
                {isConnected ? `Connected as ${waStatus.connectedNumber}` :
                 conflictActive ? 'Session conflict - remove other devices' :
                 isConnecting ? 'Scan QR code to connect' :
                 isOffline ? 'Service offline' : 'Disconnected'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white transition-colors"
              title="Refresh Status"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white transition-colors"
              title="Settings"
            >
              <Settings size={16} />
            </button>
            {isConnected && (
              <button
                onClick={handleLogout}
                className="px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 text-sm font-medium transition-colors"
              >
                Disconnect
              </button>
            )}
          </div>
        </div>

        {/* QR Code / Connected Info */}
        <div className="p-5">
          {isOffline ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-400 font-medium">WhatsApp service is offline</p>
              <p className="text-white/40 text-sm mt-1">Please contact support to restart the service</p>
            </div>
          ) : isConnecting && waStatus?.qrCode ? (
            <div className="flex flex-col items-center py-4">
              <div className="bg-white p-4 rounded-xl mb-4">
                <img src={waStatus.qrCode} alt="QR Code" className="w-48 h-48" />
              </div>
              <p className="text-white/60 text-sm">Scan with WhatsApp on your phone</p>
              <p className="text-white/40 text-xs mt-1">Open WhatsApp &gt; Settings &gt; Linked Devices &gt; Link a Device</p>
            </div>
          ) : isConnected ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-[#25D366]">{waStatus.analytics?.sentToday || 0}</div>
                <div className="text-xs text-white/40 mt-1">Sent Today</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{waStatus.analytics?.dailyRemaining || settings.dailyLimit}</div>
                <div className="text-xs text-white/40 mt-1">Remaining</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-amber-400">{queue.pending || 0}</div>
                <div className="text-xs text-white/40 mt-1">In Queue</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-red-400">{waStatus.analytics?.failedToday || 0}</div>
                <div className="text-xs text-white/40 mt-1">Failed</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <QrCode className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/50">Generating QR code...</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        {isConnected && (
          <div className="px-5 pb-5 flex flex-wrap gap-2">
            <button
              onClick={() => setShowBroadcastModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#25D366]/20 hover:bg-[#25D366]/30 border border-[#25D366]/30 rounded-xl text-[#25D366] text-sm font-medium transition-colors"
              data-testid="wa-broadcast-btn"
            >
              <Send size={16} />
              New Broadcast
            </button>
            <button
              onClick={() => setShowTestModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/70 text-sm font-medium transition-colors"
              data-testid="wa-test-btn"
            >
              <Smartphone size={16} />
              Test Message
            </button>
            {waStatus.isPaused ? (
              <button
                onClick={() => toggleQueue(false)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 rounded-xl text-amber-400 text-sm font-medium transition-colors"
              >
                <Play size={16} />
                Resume Queue
              </button>
            ) : (
              <button
                onClick={() => toggleQueue(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/70 text-sm font-medium transition-colors"
              >
                <Pause size={16} />
                Pause Queue
              </button>
            )}
            {queue.pending > 0 && (
              <>
                <button
                  onClick={forceProcessQueue}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-xl text-blue-400 text-sm font-medium transition-colors"
                >
                  <Send size={16} />
                  Force Send
                </button>
                <button
                  onClick={clearQueue}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm font-medium transition-colors"
                >
                  <Trash2 size={16} />
                  Clear Queue
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Templates Section */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-white">Message Templates</h3>
          <button
            onClick={() => { setShowTemplateForm(true); setEditingTemplate(null); setTemplateForm({ name: '', content: '' }); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#8B5CF6]/20 hover:bg-[#8B5CF6]/30 border border-[#8B5CF6]/30 rounded-lg text-[#8B5CF6] text-xs font-medium transition-colors"
            data-testid="wa-add-template-btn"
          >
            <Plus size={14} />
            Add Template
          </button>
        </div>
        
        <div className="p-5">
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/50 text-sm">No templates yet. Create one to start broadcasting.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {templates.map((template) => (
                <div key={template.id} className="bg-white/5 rounded-xl p-4 group">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-white">{template.name}</h4>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => { setEditingTemplate(template); setTemplateForm({ name: template.name, content: template.content }); setShowTemplateForm(true); }}
                        className="p-1.5 rounded bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="p-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm whitespace-pre-wrap">{template.content}</p>
                  {template.variables?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {template.variables.map((v, i) => (
                        <span key={i} className="px-2 py-0.5 bg-[#8B5CF6]/10 text-[#8B5CF6] text-[10px] rounded-full">{`{{${v}}}`}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Queue Section */}
      {queue.queue?.length > 0 && (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-white/10">
            <h3 className="font-heading font-semibold text-white">Message Queue</h3>
            <p className="text-white/40 text-xs mt-1">
              {queue.pending} pending • {queue.sent} sent • {queue.failed} failed
            </p>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {queue.queue.slice(0, 20).map((msg) => (
              <div key={msg.id} className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white truncate">{msg.leadName || msg.phone}</p>
                  <p className="text-xs text-white/40 truncate">{msg.message?.substring(0, 50)}...</p>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  {msg.status === 'pending' && (
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] rounded">Pending</span>
                  )}
                  {msg.status === 'sent' && (
                    <CheckCircle size={14} className="text-[#25D366]" />
                  )}
                  {msg.status === 'failed' && (
                    <XCircle size={14} className="text-red-400" title={msg.error} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Template Form Modal */}
      <AnimatePresence>
        {showTemplateForm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setShowTemplateForm(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-lg bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl"
            >
              <div className="p-6 border-b border-white/10">
                <button
                  onClick={() => setShowTemplateForm(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"
                >
                  <X size={16} className="text-white/60" />
                </button>
                <h2 className="font-heading font-bold text-xl text-white">
                  {editingTemplate ? 'Edit Template' : 'Create Template'}
                </h2>
              </div>

              <form onSubmit={handleSaveTemplate} className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Template Name</label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#25D366]/50"
                    placeholder="Welcome Message"
                    required
                  />
                </div>

                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Message Content</label>
                  <textarea
                    value={templateForm.content}
                    onChange={(e) => setTemplateForm(p => ({ ...p, content: e.target.value }))}
                    rows={5}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#25D366]/50 resize-none font-mono text-sm"
                    placeholder={`Hi {{name}},\n\nThank you for your interest in our trading strategies!\n\nBest regards,\nMonk Algo Team`}
                    required
                  />
                  <p className="text-[10px] text-white/30 mt-1">
                    Variables: {`{{name}}, {{email}}, {{phone}}`} will be auto-replaced
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowTemplateForm(false)}
                    className="flex-1 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingTemplate}
                    className="flex-1 py-3 bg-[#25D366] hover:bg-[#25D366]/90 rounded-xl text-white font-semibold flex items-center justify-center gap-2"
                  >
                    {savingTemplate ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                    {savingTemplate ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Broadcast Modal */}
      <AnimatePresence>
        {showBroadcastModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setShowBroadcastModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-2xl max-h-[90vh] bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col"
            >
              <div className="p-6 border-b border-white/10 flex-shrink-0">
                <button
                  onClick={() => setShowBroadcastModal(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"
                >
                  <X size={16} className="text-white/60" />
                </button>
                <h2 className="font-heading font-bold text-xl text-white flex items-center gap-2">
                  <Send size={20} className="text-[#25D366]" />
                  New Broadcast
                </h2>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Template Selection */}
                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Select Template</label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    className="w-full px-4 py-3 bg-[#1a1a24] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]/50 appearance-none cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                    required
                  >
                    <option value="" className="bg-[#1a1a24] text-white/50">Choose a template...</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id} className="bg-[#1a1a24] text-white">{t.name}</option>
                    ))}
                  </select>
                </div>

                {/* Schedule (optional) */}
                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Schedule (optional)</label>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="w-full px-4 py-3 bg-[#1a1a24] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]/50"
                    style={{ colorScheme: 'dark' }}
                  />
                  <p className="text-[10px] text-white/30 mt-1">Leave empty to send immediately</p>
                </div>

                {/* Lead Selection */}
                <div>
                  {/* Filter Controls */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <select
                      value={filterTag}
                      onChange={(e) => { setFilterTag(e.target.value); setSelectedLeadIds([]); }}
                      className="px-3 py-1.5 bg-[#1a1a24] border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:border-[#8B5CF6]/50"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="all" className="bg-[#1a1a24]">All Tags</option>
                      <option value="pine" className="bg-[#1a1a24]">Pine Script</option>
                      <option value="tester" className="bg-[#1a1a24]">Tester</option>
                    </select>
                    <select
                      value={filterDate}
                      onChange={(e) => { setFilterDate(e.target.value); setSelectedLeadIds([]); }}
                      className="px-3 py-1.5 bg-[#1a1a24] border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:border-[#8B5CF6]/50"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="all" className="bg-[#1a1a24]">All Time</option>
                      <option value="today" className="bg-[#1a1a24]">Today</option>
                      <option value="week" className="bg-[#1a1a24]">Last 7 Days</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-white/50">
                      Select Recipients ({selectedLeadIds.length} of {getFilteredLeads().filter(leadHasPhone).length} with phone)
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllLeads}
                        className="text-xs text-[#25D366] hover:underline"
                      >
                        Select all
                      </button>
                      <span className="text-white/20">|</span>
                      <button
                        type="button"
                        onClick={selectNone}
                        className="text-xs text-white/50 hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto bg-[#1a1a24] rounded-lg border border-white/10">
                    {getFilteredLeads().length === 0 ? (
                      <p className="text-white/40 text-sm text-center py-4">No leads match filters</p>
                    ) : (
                      getFilteredLeads().map(lead => {
                        const hasPhone = leadHasPhone(lead);
                        const phoneDisplay = getLeadPhone(lead) || 'No phone';
                        return (
                          <label
                            key={lead.id}
                            className={`flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 cursor-pointer border-b border-white/5 last:border-0 ${!hasPhone ? 'opacity-50' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedLeadIds.includes(lead.id)}
                              onChange={() => hasPhone && toggleLeadSelection(lead.id)}
                              disabled={!hasPhone}
                              className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#25D366] focus:ring-[#25D366]/50 disabled:opacity-30"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{lead.name}</p>
                              <p className={`text-xs truncate ${hasPhone ? 'text-white/40' : 'text-red-400/60'}`}>{phoneDisplay}</p>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                              lead.tier === 'HOT' ? 'bg-red-500/20 text-red-400' :
                              lead.tier === 'WARM' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>{lead.tier || 'COLD'}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <p className="text-[10px] text-white/30 mt-1">
                    Showing {getFilteredLeads().length} leads ({getFilteredLeads().filter(leadHasPhone).length} with phone)
                  </p>
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowBroadcastModal(false)}
                  className="flex-1 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBroadcast}
                  disabled={broadcasting || !selectedTemplateId || selectedLeadIds.length === 0}
                  className="flex-1 py-3 bg-[#25D366] hover:bg-[#25D366]/90 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {broadcasting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {broadcasting ? 'Sending...' : `Send to ${selectedLeadIds.length} leads`}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Test Message Modal */}
      <AnimatePresence>
        {showTestModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setShowTestModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-md bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl"
            >
              <div className="p-6 border-b border-white/10">
                <button
                  onClick={() => setShowTestModal(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"
                >
                  <X size={16} className="text-white/60" />
                </button>
                <h2 className="font-heading font-bold text-xl text-white">Send Test Message</h2>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Phone Number (with country code)</label>
                  <input
                    type="text"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#25D366]/50"
                    placeholder="+919876543210"
                  />
                </div>

                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Message</label>
                  <textarea
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#25D366]/50 resize-none"
                    placeholder="Hello! This is a test message from Monk Algo."
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowTestModal(false)}
                    className="flex-1 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendTest}
                    disabled={sendingTest || !testPhone || !testMessage}
                    className="flex-1 py-3 bg-[#25D366] hover:bg-[#25D366]/90 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {sendingTest ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    {sendingTest ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setShowSettings(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-md bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl"
            >
              <div className="p-6 border-b border-white/10">
                <button
                  onClick={() => setShowSettings(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"
                >
                  <X size={16} className="text-white/60" />
                </button>
                <h2 className="font-heading font-bold text-xl text-white flex items-center gap-2">
                  <Settings size={20} className="text-white/60" />
                  Broadcast Settings
                </h2>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Daily Message Limit</label>
                  <input
                    type="number"
                    value={settings.dailyLimit}
                    onChange={(e) => setSettings(s => ({ ...s, dailyLimit: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#8B5CF6]/50"
                    min={1}
                    max={1000}
                  />
                  <p className="text-[10px] text-white/30 mt-1">Recommended: 50-200 to avoid being flagged</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-white/50 mb-1.5 block">Min Delay (seconds)</label>
                    <input
                      type="number"
                      value={settings.minDelay}
                      onChange={(e) => setSettings(s => ({ ...s, minDelay: parseInt(e.target.value) || 30 }))}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#8B5CF6]/50"
                      min={30}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1.5 block">Max Delay (seconds)</label>
                    <input
                      type="number"
                      value={settings.maxDelay}
                      onChange={(e) => setSettings(s => ({ ...s, maxDelay: parseInt(e.target.value) || 60 }))}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#8B5CF6]/50"
                      min={30}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-white/50 mb-1.5 block">Business Hours Start</label>
                    <select
                      value={settings.businessHoursStart}
                      onChange={(e) => setSettings(s => ({ ...s, businessHoursStart: parseInt(e.target.value) }))}
                      className="w-full px-4 py-3 bg-[#1a1a24] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#8B5CF6]/50"
                      style={{ colorScheme: 'dark' }}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i} className="bg-[#1a1a24] text-white">{i.toString().padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1.5 block">Business Hours End</label>
                    <select
                      value={settings.businessHoursEnd}
                      onChange={(e) => setSettings(s => ({ ...s, businessHoursEnd: parseInt(e.target.value) }))}
                      className="w-full px-4 py-3 bg-[#1a1a24] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#8B5CF6]/50"
                      style={{ colorScheme: 'dark' }}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i} className="bg-[#1a1a24] text-white">{i.toString().padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-white">Enable Broadcasting</span>
                  <button
                    type="button"
                    onClick={() => setSettings(s => ({ ...s, enabled: !s.enabled }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${settings.enabled ? 'bg-[#25D366]' : 'bg-white/20'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${settings.enabled ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowSettings(false)}
                    className="flex-1 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="flex-1 py-3 bg-[#8B5CF6] hover:bg-[#8B5CF6]/90 rounded-xl text-white font-semibold flex items-center justify-center gap-2"
                  >
                    {savingSettings ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                    {savingSettings ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
